import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();

/**
 * Address schema (primary address)
 */
const AddressSchema = z
  .object({
    city: z.string(),
    state: z.string(),
    street: z.string(),
    country: z.string(),
    postalCode: z.string(),
  })
  .nullable();

/**
 * Full address schema (for addresses array)
 */
const FullAddressSchema = z.object({
  id: UUIDSchema,
  recipientName: z.string().nullable().optional(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable().optional(),
  locality: z.string(),
  administrativeArea: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  countryIso31661Alpha2: z.string(),
  fullNormalizedAddress: z.string().nullable().optional(),
});

/**
 * Alias schema
 */
const AliasSchema = z.object({
  id: UUIDSchema,
  alias: z.string(),
  addedByUserId: z.string().nullable().optional(),
  createdAt: z.date(),
});

/**
 * Contact schema
 */
const ContactSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  title: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  isPrimary: z.boolean(),
});

/**
 * Supplier Product schema
 */
const SupplierProductSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  unitPrice: z.number(),
  product: z.object({
    id: UUIDSchema,
    name: z.string(),
    sku: z.string().nullable(),
    description: z.string().nullable(),
  }),
  productVariant: z
    .object({
      id: UUIDSchema,
      sku: z.string().nullable(),
    })
    .optional(),
});

/**
 * Internal Purchase Order schema
 */
const InternalPurchaseOrderSchema = z.object({
  id: UUIDSchema,
  internalId: z.number(),
  externalId: z.string(),
  orderDate: z.date(),
  deliveryDate: z.date().nullable(),
  status: z.string(),
  totalAmount: z.number().nullable(),
  lineItems: z.array(
    z.object({
      id: UUIDSchema,
      quantity: z.number(),
      unitPrice: z.number(),
      totalPrice: z.number(),
      product: z.object({
        id: UUIDSchema,
        name: z.string(),
      }),
    }),
  ),
});

/**
 * Supplier schema for RPC responses
 */
export const SupplierSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  contactName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: AddressSchema,
  paymentTerms: z.string().nullable(),
  preferredPaymentTerms: z.string().nullable(),
  preferredOrderMethod: z.string().nullable(),
  taxId: z.string().nullable(),
  status: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  companyId: UUIDSchema.nullable(),
  // Optional associations
  addresses: z.array(FullAddressSchema).optional(),
  aliases: z.array(AliasSchema).optional(),
  contacts: z.array(ContactSchema).optional(),
  products: z.array(SupplierProductSchema).optional(),
  internalPurchaseOrders: z.array(InternalPurchaseOrderSchema).optional(),
});

/**
 * Input schema for getting a supplier
 */
export const GetSupplierInputSchema = z.object({
  id: UUIDSchema,
  include: z
    .array(
      z.enum([
        "products",
        "contacts",
        "internalPurchaseOrders",
        "addresses",
        "aliases",
      ]),
    )
    .optional(),
});

/**
 * Response schema for getting a supplier
 */
export const GetSupplierResponseSchema = SupplierSchema;

/**
 * Lightweight supplier lookup for pickers / filter comboboxes.
 * `ids` lets a UI resolve names for already-selected suppliers.
 */
export const SearchSuppliersInputSchema = z.object({
  search: z.string().optional(),
  ids: z.array(UUIDSchema).optional(),
  limit: z.int().min(1).max(100).optional(),
});

export const SearchSuppliersResponseSchema = z.object({
  suppliers: z.array(z.object({ id: UUIDSchema, name: z.string() })),
});

export const SupplierStatusSchema = z.enum(["active", "inactive", "on_hold"]);

export const ListSuppliersInputSchema = z.object({
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(100).default(50),
  search: z.string().optional(),
  /** Default false. Inactive vendors stay off the list until this is set. */
  includeInactive: z.boolean().default(false),
});

export const SupplierListItemSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  email: z.string().nullable(),
  status: SupplierStatusSchema,
  originId: z.string().nullable(),
  companyId: UUIDSchema,
  address: z.string().nullable(),
});

export const ListSuppliersResponseSchema = z.object({
  suppliers: z.array(SupplierListItemSchema),
  totalCount: z.number().int().nonnegative(),
});

export const suppliersContracts = {
  list: oc
    .input(ListSuppliersInputSchema)
    .output(ListSuppliersResponseSchema)
    .route({
      method: "GET",
      summary: "List suppliers",
      description:
        "Paged supplier list for the organization. Inactive suppliers are omitted unless includeInactive is set. Supports a name search.",
    }),
  search: oc
    .input(SearchSuppliersInputSchema)
    .output(SearchSuppliersResponseSchema)
    .route({
      method: "GET",
      summary: "Search suppliers by name",
      description:
        "Lightweight supplier lookup returning IDs and names, by name search or a list of IDs. For pickers and filters.",
    }),
  get: oc
    .input(GetSupplierInputSchema)
    .output(GetSupplierResponseSchema)
    .route({
      method: "GET",
      summary: "Get a supplier",
      description:
        "Returns a supplier's contact info, address, payment terms, tax ID, and status. Optionally includes products, contacts, purchase orders, addresses, and aliases.",
    }),
} as const;

export type Supplier = z.infer<typeof SupplierSchema>;
export type GetSupplierInput = z.infer<typeof GetSupplierInputSchema>;
export type GetSupplierResponse = z.infer<typeof GetSupplierResponseSchema>;
export type SearchSuppliersInput = z.infer<typeof SearchSuppliersInputSchema>;
export type SearchSuppliersResponse = z.infer<
  typeof SearchSuppliersResponseSchema
>;
